
import { Doctor } from '../types';
import { doctorServiceMock } from './doctorServiceMock';
import { useMockData } from '../utils/mockStorageUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

// Map Supabase doctor data to our app's Doctor type
const mapSupabaseDoctor = (doctor: any): Doctor => {
  return {
    id: doctor.id,
    name: doctor.name,
    specialty: doctor.specialty,
    image: doctor.image,
    bio: doctor.bio || '',
    weeklyOffDays: doctor.weekly_off_days || [],
    slotDuration: doctor.slot_duration || 30,
    startTime: doctor.start_time || "09:00",
    endTime: doctor.end_time || "17:00"
  };
};

// Cache for doctor data to improve performance
let doctorCache: Doctor[] | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 60000; // 1 minute cache TTL

export const doctorService = {
  // Reset all mock doctor data and clear cache
  resetAll: async (): Promise<void> => {
    doctorCache = null;
    
    try {
      // Clear Supabase doctors table
      const { error } = await supabase
        .from('doctors')
        .delete()
        .not('id', 'is', null);
        
      if (error) throw error;
      
      // Also reset local storage for mock data
      await doctorServiceMock.resetAll();
    } catch (error: any) {
      // In development with mock data enabled, just clear localStorage
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        await doctorServiceMock.resetAll();
      } else {
        console.error('Error resetting doctors data:', error);
        throw error;
      }
    }
  },

  // Get all doctors with caching for performance
  getAll: async (forceRefresh = false): Promise<Doctor[]> => {
    // Return cached data if available and not expired
    const now = Date.now();
    if (!forceRefresh && doctorCache && (now - lastFetchTime < CACHE_TTL)) {
      console.log('Using cached doctor data');
      return doctorCache;
    }
    
    try {
      // Set a short timeout for the fetch to prevent long waits
      const fetchTimeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Fetch timeout")), 4000);
      });
      
      // Create the fetch promise
      const fetchPromise = supabase
        .from('doctors')
        .select('*')
        .order('name');
      
      // Race between fetch and timeout
      const { data, error } = await Promise.race([
        fetchPromise,
        fetchTimeoutPromise.then(() => {
          throw new Error("Fetch timeout");
        })
      ]) as any;
      
      if (error) throw error;
      
      const doctors = data.map(mapSupabaseDoctor);
      
      // Update cache
      doctorCache = doctors;
      lastFetchTime = now;
      
      return doctors;
    } catch (error: any) {
      console.error('Error fetching doctors:', error);
      
      // If we have cached data, return it even if expired in case of error
      if (doctorCache) {
        console.log('Falling back to cached doctor data due to error');
        return doctorCache;
      }
      
      // Handle network errors in development with mock data if explicitly enabled
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock doctors data due to error (mock data enabled).');
        return doctorServiceMock.getAll();
      }
      
      throw error;
    }
  },
  
  // Get doctor by ID
  getById: async (id: number | string): Promise<Doctor> => {
    try {
      // Check cache first for faster response
      if (doctorCache) {
        const cachedDoctor = doctorCache.find(d => d.id.toString() === id.toString());
        if (cachedDoctor) {
          return cachedDoctor;
        }
      }
      
      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .eq('id', id.toString())
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error(`Doctor with ID ${id} not found`);
      
      return mapSupabaseDoctor(data);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock doctor data due to error. In production, this would fail.');
        return doctorServiceMock.getById(typeof id === 'string' ? parseInt(id) : id);
      }
      console.error('Error fetching doctor:', error);
      throw error;
    }
  },
  
  // Create new doctor with file upload support
  create: async (doctorData: FormData | Omit<Doctor, 'id'>): Promise<Doctor> => {
    try {
      let imageUrl = '';
      
      // Handle image upload if it exists
      if (doctorData instanceof FormData && doctorData.get('profileImage')) {
        const file = doctorData.get('profileImage') as File;
        const fileExt = file.name.split('.').pop();
        const filePath = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        
        // Upload file to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('profile_images')
          .upload(filePath, file);
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: publicUrlData } = supabase.storage
          .from('profile_images')
          .getPublicUrl(filePath);
          
        imageUrl = publicUrlData.publicUrl;
      } else if (doctorData instanceof FormData && doctorData.get('image')) {
        imageUrl = doctorData.get('image') as string;
      } else if (!(doctorData instanceof FormData) && 'image' in doctorData) {
        imageUrl = (doctorData as Omit<Doctor, 'id'>).image || '';
      }
      
      // Prepare doctor data for insertion
      const doctorRecord = doctorData instanceof FormData
        ? {
            name: doctorData.get('name') as string,
            specialty: doctorData.get('specialty') as string,
            bio: doctorData.get('bio') as string || '',
            image: imageUrl,
            weekly_off_days: doctorData.get('weeklyOffDays') 
              ? JSON.parse(doctorData.get('weeklyOffDays') as string) 
              : [],
            slot_duration: doctorData.get('slotDuration') 
              ? parseInt(doctorData.get('slotDuration') as string) 
              : 30,
            start_time: doctorData.get('startTime') as string || '09:00',
            end_time: doctorData.get('endTime') as string || '17:00'
          }
        : {
            name: (doctorData as Omit<Doctor, 'id'>).name,
            specialty: (doctorData as Omit<Doctor, 'id'>).specialty,
            bio: (doctorData as Omit<Doctor, 'id'>).bio || '',
            image: imageUrl,
            weekly_off_days: (doctorData as Omit<Doctor, 'id'>).weeklyOffDays || [],
            slot_duration: (doctorData as Omit<Doctor, 'id'>).slotDuration || 30,
            start_time: (doctorData as Omit<Doctor, 'id'>).startTime || '09:00',
            end_time: (doctorData as Omit<Doctor, 'id'>).endTime || '17:00'
          };
      
      // Insert doctor record
      const { data, error } = await supabase
        .from('doctors')
        .insert(doctorRecord)
        .select()
        .single();
      
      if (error) throw error;
      
      // Update the cache with the new doctor
      if (doctorCache) {
        doctorCache.push(mapSupabaseDoctor(data));
      }
      
      return mapSupabaseDoctor(data);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock create response due to error. In production, this would fail.');
        return doctorServiceMock.create(doctorData);
      }
      console.error('Error creating doctor:', error);
      throw error;
    }
  },
  
  // Update doctor with file upload support
  update: async (id: number | string, doctorData: FormData | Partial<Doctor>): Promise<Doctor> => {
    try {
      let imageUrl = undefined;
      
      // Handle image upload if it exists
      if (doctorData instanceof FormData && doctorData.get('profileImage')) {
        const file = doctorData.get('profileImage') as File;
        const fileExt = file.name.split('.').pop();
        const filePath = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        
        // Upload file to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('profile_images')
          .upload(filePath, file);
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: publicUrlData } = supabase.storage
          .from('profile_images')
          .getPublicUrl(filePath);
          
        imageUrl = publicUrlData.publicUrl;
      } else if (doctorData instanceof FormData && doctorData.get('image')) {
        imageUrl = doctorData.get('image') as string;
      } else if (!(doctorData instanceof FormData) && 'image' in doctorData) {
        imageUrl = (doctorData as Partial<Doctor>).image;
      }
      
      // Prepare doctor data for update
      const doctorRecord: any = {};
      
      if (doctorData instanceof FormData) {
        if (doctorData.get('name')) doctorRecord.name = doctorData.get('name');
        if (doctorData.get('specialty')) doctorRecord.specialty = doctorData.get('specialty');
        if (doctorData.get('bio')) doctorRecord.bio = doctorData.get('bio');
        if (imageUrl) doctorRecord.image = imageUrl;
        if (doctorData.get('weeklyOffDays')) {
          doctorRecord.weekly_off_days = JSON.parse(doctorData.get('weeklyOffDays') as string);
        }
        if (doctorData.get('slotDuration')) {
          doctorRecord.slot_duration = parseInt(doctorData.get('slotDuration') as string);
        }
        if (doctorData.get('startTime')) doctorRecord.start_time = doctorData.get('startTime');
        if (doctorData.get('endTime')) doctorRecord.end_time = doctorData.get('endTime');
      } else {
        const typedData = doctorData as Partial<Doctor>;
        if (typedData.name !== undefined) doctorRecord.name = typedData.name;
        if (typedData.specialty !== undefined) doctorRecord.specialty = typedData.specialty;
        if (typedData.bio !== undefined) doctorRecord.bio = typedData.bio;
        if (imageUrl !== undefined) doctorRecord.image = imageUrl;
        if (typedData.weeklyOffDays !== undefined) doctorRecord.weekly_off_days = typedData.weeklyOffDays;
        if (typedData.slotDuration !== undefined) doctorRecord.slot_duration = typedData.slotDuration;
        if (typedData.startTime !== undefined) doctorRecord.start_time = typedData.startTime;
        if (typedData.endTime !== undefined) doctorRecord.end_time = typedData.endTime;
      }
      
      // Update doctor record
      const { data, error } = await supabase
        .from('doctors')
        .update(doctorRecord)
        .eq('id', id.toString())
        .select()
        .single();
      
      if (error) throw error;
      
      // Update cache if it exists
      const updatedDoctor = mapSupabaseDoctor(data);
      if (doctorCache) {
        const index = doctorCache.findIndex(d => d.id.toString() === id.toString());
        if (index >= 0) {
          doctorCache[index] = updatedDoctor;
        }
      }
      
      return updatedDoctor;
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock update response due to error. In production, this would fail.');
        return doctorServiceMock.update(typeof id === 'number' ? id : parseInt(id), doctorData);
      }
      console.error('Error updating doctor:', error);
      throw error;
    }
  },
  
  // Delete doctor
  delete: async (id: number | string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('doctors')
        .delete()
        .eq('id', id.toString());
      
      if (error) throw error;
      
      // Update cache if it exists by removing the deleted doctor
      if (doctorCache) {
        doctorCache = doctorCache.filter(d => d.id.toString() !== id.toString());
      }
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock delete response due to error. In production, this would fail.');
        return doctorServiceMock.delete(typeof id === 'number' ? id : parseInt(id));
      }
      console.error('Error deleting doctor:', error);
      throw error;
    }
  },
  
  // Force refresh the cache and get latest data
  refreshCache: async (): Promise<Doctor[]> => {
    doctorCache = null;
    return doctorService.getAll(true);
  }
};
